import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { TemplateField } from "@/types/activity";
import { generateContentSchema } from "@/lib/contentSchemaGenerator";
import CardTemplateFieldEditor from "./CardTemplateFieldEditor";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Eye } from "lucide-react";
import { toast } from "sonner";
import TemplatePreviewDialog from "./TemplatePreviewDialog";

interface TemplateRow {
  id: string;
  code: string;
  label: string;
  content_fields: TemplateField[] | null;
  content_schema: Record<string, unknown> | null;
  enabled: boolean;
  is_interactive: boolean;
}

export function CardTemplateManagement() {
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSizeState] = useState(() => Number(localStorage.getItem("card-templates-page-size")) || 10);
  const setPageSize = (v: number) => { setPageSizeState(v); localStorage.setItem("card-templates-page-size", String(v)); };
  const [filterTab, setFilterTab] = useState("all");

  const [editDialog, setEditDialog] = useState<TemplateRow | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<TemplateRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewDialog, setPreviewDialog] = useState<TemplateRow | null>(null);

  // Form state
  const [formCode, setFormCode] = useState("");
  const [formLabel, setFormLabel] = useState("");
  const [formEnabled, setFormEnabled] = useState(true);
  const [formInteractive, setFormInteractive] = useState(false);
  const [formFields, setFormFields] = useState<TemplateField[]>([]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    let query = supabase.from("card_template").select("*", { count: "exact" }).order("label");

    if (filterTab === "interactive") query = query.eq("is_interactive", true);
    else if (filterTab === "non-interactive") query = query.eq("is_interactive", false);

    const { data, count, error } = await query.range(from, to);
    if (!error && data) {
      setTemplates(
        data.map((t: any) => ({
          id: t.id,
          code: t.code,
          label: t.label,
          content_fields: (t.content_fields as any)?.fields ?? t.content_fields ?? null,
          content_schema: t.content_schema as any,
          enabled: t.enabled ?? true,
          is_interactive: t.is_interactive ?? false,
        })),
      );
      setTotal(count ?? 0);
    }
    setLoading(false);
  }, [page, pageSize, filterTab]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const openCreate = () => {
    setIsNew(true);
    setFormCode("");
    setFormLabel("");
    setFormEnabled(true);
    setFormInteractive(false);
    setFormFields([]);
    setEditDialog({} as TemplateRow);
  };

  const openEdit = (t: TemplateRow) => {
    setIsNew(false);
    setFormCode(t.code);
    setFormLabel(t.label);
    setFormEnabled(t.enabled);
    setFormInteractive(t.is_interactive);
    setFormFields(t.content_fields ?? []);
    setEditDialog(t);
  };

  const toSnakeCase = (str: string) =>
    str
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");

  const handleSave = async () => {
    if (!formCode.trim() || !formLabel.trim()) {
      toast.error("Code et label requis");
      return;
    }
    if (formFields.length === 0) {
      toast.error("Au moins un champ est requis");
      return;
    }
    // Check all fields have a name
    if (formFields.some((f) => !f.name.trim())) {
      toast.error("Tous les champs doivent avoir un nom");
      return;
    }

    setSaving(true);
    const contentFields = { fields: formFields };
    const contentSchema = generateContentSchema(formFields);

    const payload = {
      code: formCode.trim(),
      label: formLabel.trim(),
      enabled: formEnabled,
      is_interactive: formInteractive,
      content_fields: contentFields as any,
      content_schema: contentSchema as any,
    };

    if (isNew) {
      const { error } = await supabase.from("card_template").insert(payload as any);
      if (error) {
        toast.error(error.message.includes("unique") ? "Ce code existe déjà" : "Erreur lors de la création");
        setSaving(false);
        return;
      }
      toast.success("Template créé");
    } else {
      const { error } = await supabase
        .from("card_template")
        .update(payload as any)
        .eq("id", editDialog!.id);
      if (error) {
        toast.error("Erreur lors de la modification");
        setSaving(false);
        return;
      }
      toast.success("Template mis à jour");
    }
    setSaving(false);
    setEditDialog(null);
    fetchTemplates();
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    const { error } = await supabase.from("card_template").delete().eq("id", deleteDialog.id);
    if (error) {
      toast.error("Impossible de supprimer : ce template est utilisé par des cartes existantes.");
      setDeleteDialog(null);
      return;
    }
    toast.success("Template supprimé");
    setDeleteDialog(null);
    fetchTemplates();
  };

  const toggleEnabled = async (t: TemplateRow) => {
    const { error } = await supabase
      .from("card_template")
      .update({ enabled: !t.enabled } as any)
      .eq("id", t.id);
    if (error) {
      toast.error("Erreur");
      return;
    }
    fetchTemplates();
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: "'DM Sans', sans-serif" }}>Templates de cartes</CardTitle>
        <Button size="sm" variant="outline" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Nouveau template
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs
          value={filterTab}
          onValueChange={(v) => {
            setFilterTab(v);
            setPage(1);
          }}
          className="mb-4"
        >
          <TabsList>
            <TabsTrigger value="all">Tous</TabsTrigger>
            <TabsTrigger value="interactive">Interactifs</TabsTrigger>
            <TabsTrigger value="non-interactive">Non interactifs</TabsTrigger>
          </TabsList>
        </Tabs>

        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : templates.length === 0 ? (
          <p className="text-muted-foreground">Aucun template trouvé.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Label</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Activé</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((tpl) => (
                  <TableRow key={tpl.id}>
                    <TableCell className="font-medium">{tpl.label}</TableCell>
                    <TableCell className="font-mono text-sm">{tpl.code}</TableCell>
                    <TableCell>
                      <Badge variant={tpl.is_interactive ? "default" : "secondary"}>
                        {tpl.is_interactive ? "Interactif" : "Non interactif"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Switch checked={tpl.enabled} onCheckedChange={() => toggleEnabled(tpl)} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => setPreviewDialog(tpl)} title="Aperçu JSON">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => openEdit(tpl)} title="Modifier">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => setDeleteDialog(tpl)}
                          title="Supprimer"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Lignes par page :</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v));
                    setPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((s) => (
                      <SelectItem key={s} value={String(s)}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>
                  {total} résultat{total > 1 ? "s" : ""}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">
                  {page} / {totalPages}
                </span>
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Create / Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{isNew ? "Nouveau template" : "Modifier le template"}</DialogTitle>
            <DialogDescription>
              {isNew ? "Créez un nouveau template de carte." : `Modification de « ${editDialog?.label ?? ""} »`}
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-y-auto pr-2">
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    Code <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    value={formCode}
                    onChange={(e) => setFormCode(toSnakeCase(e.target.value))}
                    placeholder="mon_template"
                    className="font-mono"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    Label <span className="text-destructive">*</span>
                  </Label>
                  <Input value={formLabel} onChange={(e) => setFormLabel(e.target.value)} placeholder="Mon template" />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Switch checked={formInteractive} onCheckedChange={setFormInteractive} id="form-interactive" />
                  <Label htmlFor="form-interactive">Interactif</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={formEnabled} onCheckedChange={setFormEnabled} id="form-enabled" />
                  <Label htmlFor="form-enabled">Activé</Label>
                </div>
              </div>

              <div className="border-t border-border pt-4">
                <CardTemplateFieldEditor fields={formFields} onChange={setFormFields} />
              </div>
            </div>
          </div>
          <DialogFooter className="flex-shrink-0">
            <Button variant="outline" onClick={() => setEditDialog(null)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Enregistrement…" : isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce template ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le template « {deleteDialog?.label} » ({deleteDialog?.code}) sera définitivement supprimé. Cette action
              est irréversible et échouera si des cartes utilisent ce template.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Preview Dialog */}
      {previewDialog && (
        <TemplatePreviewDialog
          open={!!previewDialog}
          onOpenChange={(o) => !o && setPreviewDialog(null)}
          code={previewDialog.code}
          label={previewDialog.label}
          fields={previewDialog.content_fields ?? []}
          contentSchema={previewDialog.content_schema}
        />
      )}
    </Card>
  );
}
